import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Flag, Clock, Send } from 'lucide-react';
import ThemeToggle from '../components/ThemeToggle';

export default function TestInterface() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(0);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const fetchTest = useCallback(async () => {
    try {
      const [testRes, questionsRes] = await Promise.all([
        api.get(`/test-series/${id}`),
        api.get(`/test/${id}/questions`)
      ]);
      setTest(testRes.data);
      setQuestions(questionsRes.data);
      setTimeLeft(testRes.data.duration * 60);
    } catch (error) {
      toast.error('Failed to load test');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const handleSubmit = useCallback(async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const response = await api.post(`/test/submit/${id}`, { answers });
      toast.success('Test submitted successfully!');
      navigate(`/results/${response.data.result_id}`);
    } catch (error) {
      toast.error('Failed to submit test');
      setSubmitting(false);
    }
  }, [id, answers, submitting, navigate]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    // Redirect admin to test detail page instead of test interface
    if (user.role === 'admin') {
      toast.info('Admins can preview tests from the Admin Panel');
      navigate(`/test-series/${id}`);
      return;
    }
    fetchTest();
  }, [id, user, navigate, fetchTest]);

  useEffect(() => {
    if (timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [timeLeft, handleSubmit]);

  const handleAnswerChange = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const currentQuestion = questions[currentIndex];
  const isAnswered = currentQuestion && answers[currentQuestion.id];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading test...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="test-interface">
      {/* Header */}
      <div className="border-b border-border backdrop-blur-md bg-background/80 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-xl font-outfit font-bold">{test?.title}</h1>
              <p className="text-sm text-muted-foreground">Question {currentIndex + 1} of {questions.length}</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2 px-4 py-2 bg-card border border-border rounded-lg">
                <Clock className="w-5 h-5 text-accent" />
                <span className="font-mono font-bold text-lg" data-testid="timer">
                  {formatTime(timeLeft)}
                </span>
              </div>
              <ThemeToggle />
              <Button 
                onClick={handleSubmit} 
                disabled={submitting}
                className="bg-primary hover:bg-primary/90 text-white font-semibold"
                data-testid="submit-test-btn"
              >
                <Send className="w-4 h-4 mr-2" />
                Submit Test
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-4 gap-8">
          {/* Question Panel */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {currentQuestion && (
                <motion.div
                  key={currentQuestion.id}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="bg-card border border-border rounded-lg p-8"
                  data-testid={`question-${currentIndex}`}
                >
                  <div className="mb-6">
                    <div className="flex items-center gap-3 mb-4">
                      <span className="px-3 py-1 bg-primary/10 border border-primary/20 rounded-full text-sm font-semibold text-primary">
                        Question {currentIndex + 1}
                      </span>
                      <span className="px-3 py-1 bg-accent/10 border border-accent/20 rounded-full text-sm font-semibold text-accent capitalize">
                        {currentQuestion.question_type === 'mcq' ? 'Multiple Choice' : 'Numerical'}
                      </span>
                    </div>
                    <h2 className="text-xl font-manrope leading-relaxed">{currentQuestion.question_text}</h2>
                  </div>

                  {currentQuestion.question_type === 'mcq' ? (
                    <RadioGroup 
                      value={answers[currentQuestion.id] || ''} 
                      onValueChange={(value) => handleAnswerChange(currentQuestion.id, value)}
                      className="space-y-3"
                    >
                      {currentQuestion.options?.map((option, idx) => (
                        <div 
                          key={idx} 
                          className="flex items-center space-x-3 p-4 border border-border rounded-lg hover:bg-muted/30 transition-colors cursor-pointer"
                          data-testid={`option-${idx}`}
                        >
                          <RadioGroupItem value={option} id={`option-${idx}`} />
                          <Label htmlFor={`option-${idx}`} className="flex-1 cursor-pointer">
                            {option}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  ) : (
                    <div>
                      <Label htmlFor="numerical-answer" className="mb-2 block">Enter your answer</Label>
                      <Input
                        id="numerical-answer"
                        type="text"
                        placeholder="Type your answer here"
                        value={answers[currentQuestion.id] || ''}
                        onChange={(e) => handleAnswerChange(currentQuestion.id, e.target.value)}
                        className="max-w-md bg-input/50 border-border focus:border-primary"
                        data-testid="numerical-input"
                      />
                    </div>
                  )}

                  {/* Navigation */}
                  <div className="flex justify-between items-center mt-8 pt-6 border-t border-border">
                    <Button
                      variant="outline"
                      onClick={() => setCurrentIndex(prev => Math.max(0, prev - 1))}
                      disabled={currentIndex === 0}
                      data-testid="prev-question-btn"
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                    
                    {isAnswered && (
                      <span className="text-sm text-success font-semibold">Answered ✓</span>
                    )}

                    <Button
                      onClick={() => setCurrentIndex(prev => Math.min(questions.length - 1, prev + 1))}
                      disabled={currentIndex === questions.length - 1}
                      data-testid="next-question-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Question Palette */}
          <div className="lg:col-span-1">
            <div className="bg-card border border-border rounded-lg p-6 sticky top-24" data-testid="question-palette">
              <h3 className="font-outfit font-semibold mb-4">Question Palette</h3>
              
              <div className="grid grid-cols-5 gap-2 mb-6">
                {questions.map((q, idx) => {
                  const answered = answers[q.id];
                  return (
                    <button
                      key={q.id}
                      onClick={() => setCurrentIndex(idx)}
                      className={`
                        aspect-square rounded-md font-semibold text-sm transition-colors
                        ${idx === currentIndex 
                          ? 'bg-primary text-white shadow-glow' 
                          : answered 
                            ? 'bg-success/20 text-success border border-success/30 hover:bg-success/30'
                            : 'bg-muted text-muted-foreground border border-border hover:bg-muted/80'
                        }
                      `}
                      data-testid={`palette-btn-${idx}`}
                    >
                      {idx + 1}
                    </button>
                  );
                })}
              </div>

              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-success/20 border border-success/30"></div>
                  <span className="text-muted-foreground">Answered ({Object.keys(answers).length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-muted border border-border"></div>
                  <span className="text-muted-foreground">Not Answered ({questions.length - Object.keys(answers).length})</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-primary"></div>
                  <span className="text-muted-foreground">Current</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
